import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import LegacyButton from '../../../components/Button';
import Select from '@/shared/components/Select';
import Button from '@/shared/components/Button';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import '@/styles/design-system.css';
import {
  ROUTE_BACK_OFFICE_PROMPT_TEMPLATES,
  ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT,
  ROUTE_BACK_OFFICE_SYSTEM_PROMPTS,
} from '../../../constants';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { SERVER_URL } from '../../../utils/apiUtilsConstants';
import { usePromptTemplate } from './usePromptTemplate';
import { useLLMConfig } from './useLLMConfig';
import { fetchAvailableVariables } from './promptTemplateUtils';
import { isContentPromptCategory } from './promptCategoryUtils';
import PromptTemplateDetails from './PromptTemplateDetails';
import PromptTemplateLLM from './PromptTemplateLLM';
import PromptTemplatePlayground from './PromptTemplatePlayground';
import './PromptTemplate.css';

const INTENT_OPTIONS = [
  { value: 'Informational', label: 'Informational' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'Transactional', label: 'Transactional' },
  { value: 'Navigational', label: 'Navigational' },
];

const PromptTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState('details');
  const [saving, setSaving] = useState(false);
  const [availableAreaVariables, setAvailableAreaVariables] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [selectedVertical, setSelectedVertical] = useState(searchParams.get('vertical') ?? '');
  const [selectedIntent, setSelectedIntent] = useState(searchParams.get('intent') ?? '');
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);

  const {
    record,
    templatesByType,
    loading,
    versions,
    selectedVersionId,
    onFieldChange,
    onTemplateChange,
    onVersionSelectChange,
    reload,
  } = usePromptTemplate(id, selectedVertical, selectedIntent);

  const isOverridable = isContentPromptCategory(record?.category);

  // When a scoped override row is opened from the list, preselect its
  // vertical/intent once — otherwise the editor silently shows the base
  // prompt. Only on first load: the user can still switch back to Base after.
  const verticalSyncedRef = useRef(Boolean(searchParams.get('vertical')));
  const intentSyncedRef = useRef(Boolean(searchParams.get('intent')));
  useEffect(() => {
    if (!record) return;
    if (!verticalSyncedRef.current) {
      verticalSyncedRef.current = true;
      if (record.rowMasterCompanyTypeId) {
        setSelectedVertical(String(record.rowMasterCompanyTypeId));
      }
    }
    if (!intentSyncedRef.current) {
      intentSyncedRef.current = true;
      if (record.rowIntent) {
        setSelectedIntent(record.rowIntent);
      }
    }
  }, [record]);

  useEffect(() => {
    const loadVerticals = async () => {
      try {
        const res = await fetch(`${SERVER_URL}/api/ui/bo/instruction-blocks/meta`, {
          credentials: 'include',
        });
        if (res.ok) {
          const meta = await res.json();
          setVerticals(meta?.verticals ?? []);
        }
      } catch (error) {
        console.error('PromptTemplate: failed to load verticals', error);
      }
    };
    loadVerticals();
  }, []);

  const {
    llmProviders,
    sections,
    primarySection,
    updateSection,
  } = useLLMConfig(record?.category, record?.name);


  useEffect(() => {
    const loadVariables = async () => {
      if (record?.category) {
        const variables = await fetchAvailableVariables(record.category, record.name);
        setAvailableAreaVariables(variables);
      } else {
        setAvailableAreaVariables([]);
      }
    };
    loadVariables();
  }, [record?.category, record?.name]);

  const savePromptTemplateGroup = async (payload) => {
    const res = await fetch(`${SERVER_URL}/api/ui/bo/prompts/group`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload ?? {}),
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      throw new Error(errorBody?.message || 'Failed to save prompt template.');
    }
    return res.json();
  };

  const onSave = async () => {
    if (!record) return;
    if (!record.category || !record.name) {
      showErrorMessage('Area and Template Name are required.');
      return;
    }
    if (!templatesByType.system.template || !templatesByType.system.template.trim()) {
      showErrorMessage('System Prompt is required.');
      return;
    }
    setSaving(true);
    try {
      const promptsPayload = {};
      Object.keys(templatesByType).forEach((type) => {
        promptsPayload[type] = {
          template: templatesByType[type]?.template ?? '',
        };
      });

      const payload = {
        primaryPromptId: record.id ?? null,
        category: record.category,
        name: record.name,
        variablesHelp: record.variablesHelp ?? '',
        masterCompanyTypeId: isOverridable && selectedVertical ? Number(selectedVertical) : null,
        intent: isOverridable && selectedIntent ? selectedIntent : null,
        prompts: promptsPayload,
        llmConfigs: sections
          .filter((section) => section.promptId)
          .map((section) => ({
            promptId: section.promptId,
            provider: section.selectedProvider || null,
            model: section.selectedModel || null,
            temperature: section.llmTemperature ?? null,
            maxTokens: section.llmMaxTokens ?? null,
            fallbackProvider: section.fallbackProvider || null,
            fallbackModel: section.fallbackModel || null,
          })),
      };

      const result = await savePromptTemplateGroup(payload);

      showSuccessMessage((isOverridable && (selectedVertical || selectedIntent))
        ? 'Prompt override saved.'
        : 'Prompt template saved.');

      const savedId = result?.item?.id;
      if (savedId && String(savedId) !== String(id)) {
        const suffixParams = new URLSearchParams();
        if (isOverridable && selectedVertical) suffixParams.set('vertical', selectedVertical);
        if (isOverridable && selectedIntent) suffixParams.set('intent', selectedIntent);
        const suffix = suffixParams.toString() ? `?${suffixParams}` : '';
        navigate(`${ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT}${savedId}${suffix}`, { replace: true });
      } else {
        await reload();
      }
    } catch (error) {
      console.error('PromptTemplate: Error saving template:', error);
      showErrorMessage(error?.message || 'Failed to save prompt template.');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    if (isOverridable) {
      navigate(ROUTE_BACK_OFFICE_PROMPT_TEMPLATES);
    } else {
      navigate(ROUTE_BACK_OFFICE_SYSTEM_PROMPTS);
    }
  };

  const onRevertToBase = async () => {
    const params = new URLSearchParams({
      category: record.category,
      name: record.name,
      masterCompanyTypeId: selectedVertical,
      intent: selectedIntent,
    });
    const res = await fetch(`${SERVER_URL}/api/ui/bo/prompts/group?${params}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      showErrorMessage(errorBody?.message || 'Failed to revert to base prompt.');
      return;
    }
    showSuccessMessage('Override deleted — this scope now uses the base prompt.');
    setRevertDialogOpen(false);
    setSelectedVertical('');
    setSelectedIntent('');
    if (record.basePrimaryId) {
      navigate(`${ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT}${record.basePrimaryId}`, { replace: true });
    } else {
      navigate(ROUTE_BACK_OFFICE_PROMPT_TEMPLATES);
    }
  };

  const sanitizeFilenamePart = (value) =>
    (value || '')
      .toString()
      .trim()
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();

  const buildMarkdownExport = () => {
    const promptTitle = record?.name || 'Untitled Prompt';
    const categoryTitle = record?.category || 'Uncategorized';
    const now = new Date();
    const timestamp = now.toLocaleString();
    const prompts = templatesByType || {};

    const promptSections = Object.keys(prompts).map((type) => {
      const template = prompts[type]?.template ?? '';
      return `## ${type}\n\n\`\`\`text\n${template}\n\`\`\``;
    });

    return [
      `# ${promptTitle}`,
      ``,
      `Category: ${categoryTitle}`,
      `Exported: ${timestamp}`,
      ``,
      ...promptSections,
      ``,
    ].join('\n');
  };

  const onExportMarkdown = () => {
    if (!record?.name) {
      showErrorMessage('Prompt name is required to export.');
      return;
    }
    const now = new Date();
    const fileTimestamp = now
      .toISOString()
      .replace(/[:.]/g, '-')
      .replace('T', '_')
      .replace('Z', '');
    const namePart = sanitizeFilenamePart(record.name) || 'prompt';
    const categoryPart = sanitizeFilenamePart(record.category) || 'general';
    const filename = `${namePart}-${categoryPart}-${fileTimestamp}.md`;

    const markdown = buildMarkdownExport();
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  if (loading || !record) {
    return <div>Loading...</div>;
  }

  const selectedVerticalName = selectedVertical
    ? verticals.find((v) => String(v.id) === String(selectedVertical))?.name ?? selectedVertical
    : null;

  const verticalOptions = verticals.map((v) => ({ value: String(v.id), label: v.name }));
  const isScoped = isOverridable && Boolean(selectedVertical || selectedIntent);
  const scopeLabel = [selectedVerticalName, selectedIntent].filter(Boolean).join(' / ');

  const versionOptions = versions.map((v) => {
    const dateLabel = v.createdOn ? new Date(v.createdOn).toLocaleString() : '';
    const userLabel = v.updatedByName ? ` • ${v.updatedByName}` : '';
    const snapshotPrompts = v.snapshot?.prompts ?? {};
    const typeKeys = Object.keys(snapshotPrompts).filter(
      (type) => snapshotPrompts[type]?.template
    );
    const typeLabel = typeKeys.length ? ` • ${typeKeys.join(', ')}` : '';
    return { value: String(v.id), label: `${dateLabel}${userLabel}${typeLabel}` };
  });

  return (
    <div className="client-area">
      <div className="th">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="mr-auto text-xl font-semibold text-foreground">
            {id
              ? `Edit Prompt: ${record.category ?? ''} / ${record.name ?? ''}`
              : 'Add Prompt Template'}
          </h2>
          {isOverridable && (
            isScoped ? (
              <span className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                Scope: {scopeLabel}
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                Base (all verticals / intents)
              </span>
            )
          )}
        </div>
        {isScoped && !record.hasVerticalOverride && (
          <div className="mb-4 rounded-xl border border-solid border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            No override exists for this scope yet — fields are prefilled from the base prompt.
            Saving creates the scoped version; the base prompt stays untouched.
          </div>
        )}
        {isScoped && record.hasVerticalOverride && (
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-solid border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            <span className="mr-auto">
              This scope has its own version of this prompt. Reverting deletes the override —
              it falls back to the base prompt.
            </span>
            <Button
              variant="dangerOutline"
              className="!min-h-[34px] !max-h-[34px] !bg-white px-3"
              onClick={() => setRevertDialogOpen(true)}
            >
              Revert to Base
            </Button>
          </div>
        )}
        <ConfirmDialog
          open={revertDialogOpen}
          onOpenChange={setRevertDialogOpen}
          onConfirm={onRevertToBase}
          title="Revert to base prompt?"
          description={`This permanently deletes the ${scopeLabel || ''} override for "${record.category} / ${record.name}", including its version history. This scope will use the base prompt again.`}
          confirmLabel="Delete Override"
          confirmVariant="danger"
        />
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {['details', 'llm', 'playground'].map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'primary' : 'ghost'}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'details' ? 'Details' : tab === 'llm' ? 'LLM' : 'Playground'}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {}
            {id && isOverridable && (
              <Select
                value={selectedVertical}
                onValueChange={setSelectedVertical}
                options={verticalOptions}
                placeholder="Base (all verticals)"
                clearable
                clearLabel="Base (all verticals)"
                wrapperClassName="w-56"
                label="Vertical"
                triggerClassName="!h-9"
              />
            )}
            {id && isOverridable && (
              <Select
                value={selectedIntent}
                onValueChange={setSelectedIntent}
                options={INTENT_OPTIONS}
                placeholder="Base (all intents)"
                clearable
                clearLabel="Base (all intents)"
                wrapperClassName="w-56"
                label="Intent"
                triggerClassName="!h-9"
              />
            )}
            {record.id && (
              <Select
                value={selectedVersionId}
                onValueChange={(value) => onVersionSelectChange({ target: { value } })}
                options={versionOptions}
                placeholder={versions.length > 0 ? 'Current (latest)' : 'Current (latest) – no history'}
                clearable
                clearLabel="Current (latest)"
                wrapperClassName="w-72"
                label="Version"
                triggerClassName="!h-9"
              />
            )}
            <Button variant="secondary" onClick={onExportMarkdown}>
              Export as md file
            </Button>
          </div>
        </div>
      </div>
      <div className="record-form" style={{ paddingBottom: '80px' }}>
        {activeTab === 'details' && (
          <PromptTemplateDetails
            record={record}
            templatesByType={templatesByType}
            availableAreaVariables={availableAreaVariables}
            onFieldChange={onFieldChange}
            onTemplateChange={onTemplateChange}
          />
        )}

        {activeTab === 'llm' && (
          <PromptTemplateLLM
            llmProviders={llmProviders}
            sections={sections}
            onSectionChange={updateSection}
          />
        )}

        {activeTab === 'playground' && (
          <PromptTemplatePlayground
            record={record}
            templatesByType={templatesByType}
            llmProviders={llmProviders}
            llmModels={primarySection?.models || []}
            selectedProvider={primarySection?.selectedProvider || ''}
            selectedModel={primarySection?.selectedModel || ''}
            llmTemperature={primarySection?.llmTemperature ?? 0.7}
            llmMaxTokens={primarySection?.llmMaxTokens ?? 4000}
            onProviderChange={(value) =>
              primarySection?.id ? updateSection(primarySection.id, { selectedProvider: value }) : null
            }
            onModelChange={(value) =>
              primarySection?.id ? updateSection(primarySection.id, { selectedModel: value }) : null
            }
            onTemperatureChange={(value) =>
              primarySection?.id ? updateSection(primarySection.id, { llmTemperature: value }) : null
            }
            onMaxTokensChange={(value) =>
              primarySection?.id ? updateSection(primarySection.id, { llmMaxTokens: value }) : null
            }
          />
        )}
      </div>
      
      {}
      <div className="prompt-template-sticky-buttons">
        <div className="prompt-template-sticky-buttons-container">
          <LegacyButton className="plain" onClick={onCancel} style={{ marginRight: '8px' }}>
            Cancel
          </LegacyButton>
          <LegacyButton className="primary" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </LegacyButton>
        </div>
      </div>
    </div>
  );
};

export default PromptTemplate;